import cornerstone from "cornerstone-core";
import JSZip from "jszip";
import { getOpCornerstoneElement } from "../actions/dcm_actions";

export const SCREENSHOT_TYPES = {
  IR: "ir",
  BSCAN_CURRENT: "bscanCurrent",
  ALL_BSCANS: "allBscans",
};

const CONTENT_THRESHOLD = 8;

function findContentBounds(ctx, width, height, threshold = CONTENT_THRESHOLD) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const alpha = data[index + 3];
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];

      if (
        alpha > 0 &&
        (red > threshold || green > threshold || blue > threshold)
      ) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) {
    return null;
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

function compositeElementCanvases(element, enabledElement) {
  const canvases = Array.from(element.querySelectorAll("canvas"));
  if (!canvases.length) {
    return null;
  }

  const baseCanvas = enabledElement?.canvas || canvases[0];
  const composite = document.createElement("canvas");
  composite.width = baseCanvas.width;
  composite.height = baseCanvas.height;

  const ctx = composite.getContext("2d");
  canvases.forEach((canvas) => {
    ctx.drawImage(canvas, 0, 0);
  });

  return composite;
}

/**
 * Capture the visible cornerstone viewport including tool overlays (ETDRS, lines, etc.)
 * and crop away pure-black letterboxing.
 */
export function captureCornerstoneElement(element, { refresh = false } = {}) {
  if (!element) {
    return null;
  }

  let enabledElement;
  try {
    enabledElement = cornerstone.getEnabledElement(element);
  } catch (_error) {
    return null;
  }

  if (!enabledElement?.image) {
    return null;
  }

  if (refresh) {
    try {
      cornerstone.updateImage(element);
    } catch (_error) {
      /* noop */
    }
  }

  const composite = compositeElementCanvases(element, enabledElement);
  if (!composite) {
    return null;
  }

  const crop = findContentBounds(
    composite.getContext("2d"),
    composite.width,
    composite.height
  );

  if (!crop) {
    return composite.toDataURL("image/png");
  }

  const cropped = document.createElement("canvas");
  cropped.width = crop.width;
  cropped.height = crop.height;

  const cropCtx = cropped.getContext("2d");
  cropCtx.drawImage(
    composite,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height
  );

  return cropped.toDataURL("image/png");
}

export function buildScreenshotFilename(basicMetaData, suffix = "") {
  const base = `${basicMetaData?.study_code}_${basicMetaData?.subject_code}_${basicMetaData?.visit_label}`;
  return suffix ? `${base}_${suffix}.png` : `${base}.png`;
}

export function downloadDataUrl(dataUrl, filename) {
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

function waitForImageRendered(element, timeoutMs = 5000) {
  return new Promise((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      element.removeEventListener("cornerstoneimagerendered", onRendered);
      requestAnimationFrame(() => resolve());
    };

    const onRendered = () => {
      finish();
    };

    const timer = setTimeout(finish, timeoutMs);
    element.addEventListener("cornerstoneimagerendered", onRendered);

    try {
      cornerstone.updateImage(element);
    } catch (_error) {
      clearTimeout(timer);
      finish();
    }
  });
}

async function navigateToBscanFrame(dispatch, stackId, frameIndex, element, stack) {
  dispatch({
    type: "DICOM_CHANGE_FRAME",
    changeId: stackId,
    changeType: "OP",
    frame: frameIndex,
  });

  if (!stack?.imageIds?.[frameIndex]) {
    return;
  }

  const renderPromise = waitForImageRendered(element);
  const imageId = stack.imageIds[frameIndex];
  const image = await cornerstone.loadImage(imageId);
  cornerstone.displayImage(element, image);

  if (stack.currentImageIdIndex !== frameIndex) {
    stack.currentImageIdIndex = frameIndex;
  }

  await renderPromise;
  await new Promise((resolve) => requestAnimationFrame(resolve));
}

export async function downloadIrScreenshot(dicom_data) {
  const element = getOpCornerstoneElement(dicom_data);
  await new Promise((resolve) => requestAnimationFrame(resolve));
  const dataUrl = captureCornerstoneElement(element);
  if (!dataUrl) {
    return false;
  }

  downloadDataUrl(dataUrl, buildScreenshotFilename(dicom_data?.basicMetaData));
  return true;
}

export async function downloadCurrentBscanScreenshot(dicom_data) {
  const element = dicom_data?.dicomRefBscan?.current;
  await new Promise((resolve) => requestAnimationFrame(resolve));
  const dataUrl = captureCornerstoneElement(element);
  if (!dataUrl) {
    return false;
  }

  const frameNumber = (dicom_data?.currentFrame ?? 0) + 1;
  downloadDataUrl(
    dataUrl,
    buildScreenshotFilename(
      dicom_data?.basicMetaData,
      `bscan_${String(frameNumber).padStart(3, "0")}`
    )
  );
  return true;
}

export async function downloadAllBscanScreenshots(
  dicom_data,
  dispatch,
  stackId,
  onProgress
) {
  const element = dicom_data?.dicomRefBscan?.current;
  const stack = dicom_data?.dicom_data?.[stackId]?.OPT?.stack;
  const frameCount = stack?.imageIds?.length ?? 0;

  if (!element || !frameCount) {
    return false;
  }

  const reportProgress = (payload) => {
    if (typeof onProgress === "function") {
      onProgress(payload);
    }
  };

  const originalFrame = dicom_data?.currentFrame ?? 0;
  const zip = new JSZip();
  const folder = zip.folder("bscans");
  const baseName = buildScreenshotFilename(
    dicom_data?.basicMetaData,
    "all_bscans"
  ).replace(/\.png$/, "");

  const startedAt = performance.now();
  let capturedCount = 0;

  reportProgress({
    phase: "capturing",
    current: 0,
    total: frameCount,
    etaSeconds: null,
    message: `Capturing B-scan 1 of ${frameCount}...`,
  });

  for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
    await navigateToBscanFrame(dispatch, stackId, frameIndex, element, stack);
    const dataUrl = captureCornerstoneElement(element, { refresh: true });

    if (dataUrl) {
      capturedCount += 1;
      const base64 = dataUrl.split(",")[1];
      folder.file(
        `bscan_${String(frameIndex + 1).padStart(3, "0")}.png`,
        base64,
        { base64: true }
      );
    }

    const elapsedMs = performance.now() - startedAt;
    const completed = frameIndex + 1;
    const avgMsPerFrame = elapsedMs / completed;
    const remainingFrames = frameCount - completed;
    const etaSeconds = (remainingFrames * avgMsPerFrame) / 1000;

    reportProgress({
      phase: "capturing",
      current: completed,
      total: frameCount,
      etaSeconds,
      message:
        completed < frameCount
          ? `Capturing B-scan ${completed + 1} of ${frameCount}...`
          : `Captured ${completed} of ${frameCount} B-scans.`,
    });
  }

  reportProgress({
    phase: "restoring",
    current: frameCount,
    total: frameCount,
    etaSeconds: 0,
    message: "Restoring your current B-scan view...",
  });

  await navigateToBscanFrame(dispatch, stackId, originalFrame, element, stack);

  reportProgress({
    phase: "zipping",
    current: frameCount,
    total: frameCount,
    etaSeconds: null,
    message: `Packaging ${capturedCount} screenshots into a zip file...`,
  });

  const blob = await zip.generateAsync({ type: "blob" });

  reportProgress({
    phase: "downloading",
    current: frameCount,
    total: frameCount,
    etaSeconds: 0,
    message: "Starting zip download...",
  });

  const url = URL.createObjectURL(blob);
  downloadDataUrl(url, `${baseName}.zip`);
  URL.revokeObjectURL(url);
  return capturedCount > 0;
}

export async function downloadScreenshotByType(
  type,
  dicom_data,
  dispatch,
  stackId,
  onProgress
) {
  switch (type) {
    case SCREENSHOT_TYPES.IR:
      return downloadIrScreenshot(dicom_data);
    case SCREENSHOT_TYPES.BSCAN_CURRENT:
      return downloadCurrentBscanScreenshot(dicom_data);
    case SCREENSHOT_TYPES.ALL_BSCANS:
      return downloadAllBscanScreenshots(
        dicom_data,
        dispatch,
        stackId,
        onProgress
      );
    default:
      return false;
  }
}
